import { Link, Stack, useMediaQuery } from '@mui/material';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

import colors from '../../constants/colors';
import { DirectAccessTrackProps, TrackProps } from '../../hooks/useAnalytics';

interface FooterProps {
  customerHasUbiPolicy?: boolean;
  isUserAuthenticated?: boolean;
  track: (eventName: string, properties: DirectAccessTrackProps) => void;
}

const Footer = ({
  customerHasUbiPolicy = false,
  isUserAuthenticated = false,
  track,
}: FooterProps) => {
  const theme = useTheme();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('tablet'));

  return (
    <Stack
      data-testid="footer"
      sx={{
        flexDirection: 'column',
        alignItems: { mobile: 'left', tablet: 'center' },
        p: 3,
        background: colors.csaaWhite,
      }}
    >
      <Stack
        justifyContent="center"
        alignItems={{ mobile: 'left', tablet: 'center' }}
        direction={{ mobile: 'column', tablet: 'row' }}
        sx={{
          flexWrap: 'wrap',
          mb: { mobile: 3, tablet: 2 },
        }}
      >
        {!isUserAuthenticated && (
          <Link
            component={RouterLink}
            to="/help"
            onClick={() =>
              track('FAQs/help', {
                event_type: 'Link Accessed',
                link: 'FAQs/help',
                location: 'Footer',
              })
            }
            underline="none"
            sx={{
              color: 'primary',
              fontWeight: 700,
              fontSize: { mobile: 14, tablet: 18 },
              fontFamily: 'Cabin',
              whiteSpace: 'nowrap',
            }}
          >
            FAQs and Help
          </Link>
        )}
        {!mobileBreakpoint && !isUserAuthenticated && (
          <Divider
            orientation="vertical"
            sx={{
              height: '1rem',
              mx: { mobile: 0, tablet: 2 },
            }}
          />
        )}
        <Link
          href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/terms-of-use.html"
          target="_blank"
          onClick={() =>
            track('Terms and Conditions Accessed', {
              event_type: 'Link Accessed',
              link: 'Terms of Use',
              location: 'Footer',
            })
          }
          underline="none"
          sx={{
            color: 'primary',
            fontWeight: 700,
            fontSize: { mobile: 14, tablet: 18 },
            fontFamily: 'Cabin',
            whiteSpace: 'nowrap',
          }}
        >
          Terms of Use
        </Link>
        {!mobileBreakpoint && (
          <Divider
            orientation="vertical"
            sx={{
              height: '1rem',
              mx: { mobile: 0, tablet: 2 },
            }}
          />
        )}
        <Link
          onClick={() =>
            track('Terms and Conditions Accessed', {
              event_type: 'Link Accessed',
              link: 'Privacy Policy',
              location: 'Footer',
            })
          }
          sx={{
            color: 'primary',
            fontWeight: 700,
            fontSize: { mobile: 14, tablet: 18 },
            fontFamily: 'Cabin',
            whiteSpace: 'nowrap',
          }}
          underline="none"
          href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/privacy-notice.html"
          target="_blank"
        >
          Privacy Policy
        </Link>
        {!mobileBreakpoint && (
          <Divider
            orientation="vertical"
            sx={{ height: '1rem', m: 'auto 0', mx: { mobile: 0, tablet: 2 } }}
          />
        )}
        <Link
          sx={{
            color: 'primary',
            fontWeight: 700,
            fontSize: { mobile: 14, tablet: 18 },
            fontFamily: 'Cabin',
          }}
          underline="none"
          href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/footer-nav/accessibility-statement.html"
          target="_blank"
          onClick={() =>
            track('Terms and Conditions Accessed', {
              event_type: 'Link Accessed',
              link: 'Accessibility Statement',
              location: 'Footer',
            })
          }
        >
          Accessibility Statement
        </Link>
      </Stack>
      <Stack alignItems="center">
        <Typography
          sx={{
            color: colors.csaaMid,
            fontSize: { mobile: 10, tablet: 12 },
            fontFamily: 'Cabin',
          }}
        >
          Insurance provided by CSAA Insurance Group, a AAA Insurer.
        </Typography>
        <Typography
          sx={{
            color: colors.csaaMid,
            fontSize: { mobile: 10, tablet: 12 },
            fontFamily: 'Cabin',
          }}
        >
          © {new Date().getFullYear()} CSAA Insurance Group
        </Typography>
      </Stack>
    </Stack>
  );
};

export default Footer;
